/**
 * Hook do DRE Gerencial real (Task #124 — consome /api/v2/dre do #121e).
 *
 * Diferente de useSaldoV2/useFluxoV2 (cache module-level único): o DRE
 * depende de params (regime/período/mês), então o cache é KEYED pelo
 * conjunto de params. Trocar o toggle Competência/Caixa refaz o fetch
 * (key nova) automaticamente. TTL 1 min — alinhado ao cron Omie diário.
 *
 * Em falha (rede/auth/5xx) devolve null + erro; a página decide o fallback.
 */
import { useEffect, useState } from "react";
import { getDreV2 } from "../api/client";
import type { DreV2Response } from "../api/types";
import { startVisiblePolling } from "./visiblePolling";

const REFETCH_MS = 180 * 1000; // #75: 60s->180s (fonte muda via cron diario + sync manual)

interface CacheEntry {
  expiresAt: number;
  promise: Promise<DreV2Response>;
}

const cache = new Map<string, CacheEntry>();

export function invalidateDreV2Cache(): void {
  cache.clear();
}

export interface UseDreV2Params {
  regime: string;
  periodo: string;
  mesRef?: string;
  /** "mom"/"yoy" — popula valor_comparacao (#22: Δ% da visao CFO). */
  comparar?: string;
}

export interface UseDreV2State {
  data: DreV2Response | null;
  isLoading: boolean;
  error: Error | null;
}

export function useDreV2({ regime, periodo, mesRef, comparar }: UseDreV2Params): UseDreV2State {
  const [state, setState] = useState<UseDreV2State>({
    data: null,
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    let cancel = false;
    const key = `${regime}|${periodo}|${mesRef ?? ""}|${comparar ?? ""}`;

    function load() {
      const now = Date.now();
      let entry = cache.get(key);
      if (!entry || entry.expiresAt < now) {
        entry = {
          expiresAt: now + REFETCH_MS,
          promise: getDreV2({ regime, periodo, mes_ref: mesRef, comparar }),
        };
        cache.set(key, entry);
      }
      entry.promise
        .then((data) => {
          if (cancel) return;
          setState({ data, isLoading: false, error: null });
        })
        .catch((err: Error) => {
          if (cancel) return;
          cache.delete(key);
          setState({ data: null, isLoading: false, error: err });
        });
    }

    // Reset loading ao trocar de params pra UI não mostrar dado stale do regime anterior.
    setState((s) => ({ ...s, isLoading: true }));
    load();
    // #75: tick skipa com a aba em background; volta visivel/focus -> 1 load.
    const stop = startVisiblePolling(load, REFETCH_MS);
    return () => {
      cancel = true;
      stop();
    };
  }, [regime, periodo, mesRef, comparar]);

  return state;
}
